/**
 * DATE-ONLY rendering — the one place supervision turns a calendar date into text.
 *
 * A review period, a due date, a lock date, a season bound: each names a DAY, not an instant. The
 * API serialises them as UTC midnight (`2026-05-01T00:00:00Z`) or as a bare `2026-05-01`.
 * date-fns `format(parseISO(v), ...)` renders the INSTANT in the reader's zone, so every user west
 * of Greenwich saw the previous day: Christian's screenshot of 2026-10-20 read
 * "Review Period: Apr 30 – Aug 31, 2026" for a review that starts May 1.
 *
 * The fix is the fleet pattern from ptc-tax-lifecycle (`formatNoticeDate`): `Intl.DateTimeFormat`
 * with an explicit `timeZone: 'UTC'`. That reads the UTC calendar day, so it is independent of the
 * ambient zone by construction, and it handles both shapes above.
 *
 * ⚠ Date-only fields ONLY. A true timestamp (a checklist tick, an upload, an email sent, an
 * appointment) must still render in local time — "when did this happen" is a wall-clock question
 * for the reader. `scripts/date-only-guard.test.mjs` pins which fields are which.
 */

import i18n from '../i18n';

// One pair of formatters per UI language (Spanish UI 2026-10-21): `May 1, 2026` / `1 may 2026`.
// Built once per language and kept, because constructing an Intl formatter is not free and these
// run for every row of every grid.
const formatterCache = new Map<string, { withYear: Intl.DateTimeFormat; noYear: Intl.DateTimeFormat }>();

function formatters() {
  const locale = i18n.language === 'es' ? 'es' : 'en-US';
  let f = formatterCache.get(locale);
  if (!f) {
    f = {
      withYear: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
      noYear: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    };
    formatterCache.set(locale, f);
  }
  return f;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * `May 1, 2026` (or `May 1` with `withYear: false`) for a date-only value.
 * Returns `fallback` when the value is missing or unreadable — never a guessed date.
 */
export function formatCalendarDate(
  iso: string | null | undefined,
  options: { withYear?: boolean; fallback?: string } = {}
): string {
  const { withYear = true, fallback = '' } = options;
  const d = toDate(iso);
  if (!d) return fallback;
  const f = formatters();
  return (withYear ? f.withYear : f.noYear).format(d);
}

/**
 * `May 1 – Aug 31, 2026` for a period. The start carries its own year only when the two years
 * differ (`Nov 1, 2025 – Feb 28, 2026`). Returns `fallback` if either bound is unreadable.
 */
export function formatPeriodRange(
  start: string | null | undefined,
  end: string | null | undefined,
  fallback = ''
): string {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return fallback;
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
  const f = formatters();
  return `${(sameYear ? f.noYear : f.withYear).format(s)} – ${f.withYear.format(e)}`;
}

/** `YYYY-MM-DD` of a calendar date, padded. */
function isoDay(year: number, month1: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The calendar day AFTER a date-only value, as `YYYY-MM-DD`. Pure UTC arithmetic, so it cannot be
 * shifted by the reader's zone or by a DST change. Null when the input is unreadable.
 */
export function nextCalendarDay(iso: string | null | undefined): string | null {
  const d = toDate(iso);
  if (!d) return null;
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1));
  return isoDay(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

/**
 * The last day of the month before the reader's LOCAL today, as `YYYY-MM-DD`.
 *
 * "Today" is the reader's wall-clock date, which is the right anchor for a form default. The old
 * code took that local date and then serialised it with `toISOString()`, which converts to UTC —
 * so after 20:00 ET it produced the 1st of the current month instead of the last day of the
 * previous one.
 */
export function lastDayOfPreviousMonth(now: Date = new Date()): string {
  // Day 0 of the current month is the last day of the previous month.
  const d = new Date(now.getFullYear(), now.getMonth(), 0);
  return isoDay(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** The 1st of the month `monthsBack` months before the reader's local today, as `YYYY-MM-DD`. */
export function firstOfMonthMonthsAgo(monthsBack: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  return isoDay(d.getFullYear(), d.getMonth() + 1, 1);
}
